/**
 * EmployeePicker - Wrapper around generic EntityPicker
 * 
 * Provides employee-specific configuration and maintains backward compatibility
 * with existing code that imports EmployeePickerField or EmployeePickerModal
 */

import React from 'react';
import { EntityPickerField } from '../EntityPicker/EntityPickerField';
import { EntityPickerModal } from '../EntityPicker/EntityPickerModal';

// ── Employee-specific configuration ──────────────────────────────────────────
export const EMPLOYEE_CONFIG = {
	apiEndpoint: "/api/Employee/basic-info/all/active",
	entityName: "Employee",
	idField: "id",
	displayFields: {
		primary: "fullName",
		secondary: "employeeId"
	},
	tableHeaders: ["Employee Id", "Name"],
	tableAccessors: ["employeeId", "fullName"],
	searchFields: [
		{ label: "All", field: "all", index: 0 },
		{ label: "Employee Name", field: "fullName", index: 1 },
		{ label: "Employee Id", field: "employeeId", index: 2 },
	],
	placeholder: "--Select employee--"
};

// ── EmployeePickerField wrapper ──────────────────────────────────────────────
/**
 * EmployeePickerField
 * 
 * Usage:
 * <EmployeePickerField
 *   label="Employee:"
 *   employeePersonalInformationId={form.employeeId}
 *   displayValue={form.employeeName}
 *   onSelect={(employee) => {
 *     setSelectedEmployee(employee);
 *     set("employeeId", employee.id);
 *     set("employeeName", employee.fullName);
 *   }}
 *   onClear={() => {
 *     setSelectedEmployee(null);
 *     set("employeeId", "");
 *     set("employeeName", "");
 *   }}
 *   disabled={!editable}
 *   allowClear={false}
 * />
 */
export function EmployeePickerField({
	employeePersonalInformationId,  // employee-specific prop name
	displayValue = "",
	onSelect,
	onClear,
	...rest                         // label, isRequired, error, disabled, etc.
}) {
	return (
		<EntityPickerField
			{...rest}
			config={EMPLOYEE_CONFIG}
			entityId={employeePersonalInformationId}
			displayValue={displayValue}
			onSelect={onSelect}
			onClear={onClear}
		/>
	);
}

// ── EmployeePickerModal wrapper ──────────────────────────────────────────────
/**
 * EmployeePickerModal
 * 
 * Usage (standalone - e.g., CC selection in memos):
 * <EmployeePickerModal
 *   isOpen={showCcPicker}
 *   onClose={() => setShowCcPicker(false)}
 *   setData={(employee) => {
 *     setCcList([...ccList, {
 *       id: employee.id,
 *       fullName: employee.fullName,
 *       employeeId: employee.employeeId
 *     }]);
 *   }}
 * />
 */
export function EmployeePickerModal({ isOpen, onClose, setData }) {
	return (
		<EntityPickerModal
			isOpen={isOpen}
			onClose={onClose}
			setData={setData}
			config={EMPLOYEE_CONFIG}
		/>
	);
}

// ── Exports (backward compatibility) ─────────────────────────────────────────
export { EmployeePickerField as default };